import { AggregationDefinitionFieldArray } from '@monorepo/shared/componentsV2/aggregation/AggregationDefinitionsFieldArray/AggregationDefinitionsFieldArray';
import {
  AggregationDefinitionRequest,
  AggregationMethod,
  DatasetPropertyResponse,
  DatasetResponse,
  getAggregationMethodsAllowedForPropertyType,
  PROPERTY_TYPES_THAT_CAN_BE_AGGREGATED_BY_ATLEAST_ONE_METHOD,
} from 'mapistry-shared';
import React, { useCallback } from 'react';
import { DeepPartial } from 'utility-types';
import { FormValues } from './types';

/**
 * If we are grouping by a record property, not allowing to aggregate over its child properties
 * since it's the same as grouping and aggregating the same property (we group by all child properties under the hood)
 */
function getPropertyIdsToExclude(
  allProperties: DatasetPropertyResponse[],
  groupedPropertyIdsFormValue: DeepPartial<string>[] = [],
) {
  const toExcludeSet = groupedPropertyIdsFormValue.reduce(
    (set, groupedPropertyId) => {
      if (!groupedPropertyId) {
        return set;
      }
      const descendantIds = allProperties
        .filter((p) => p.parentPropertyIdChain.includes(groupedPropertyId))
        .map((p) => p.id);

      return new Set([...set, groupedPropertyId, ...descendantIds]);
    },
    new Set<string>(),
  );

  return [...toExcludeSet];
}

interface AggregationDefinitionsProps {
  stepInputDataset: DatasetResponse;
}

export function AggregationDefinitionList({
  stepInputDataset,
}: AggregationDefinitionsProps) {
  const getAllowedColumnOptions = useCallback(
    (formValues: DeepPartial<FormValues>) => {
      const propertyIdsToExclude = getPropertyIdsToExclude(
        stepInputDataset.schemaProperties,
        formValues.groupedPropertyIds,
      );
      return stepInputDataset.schemaProperties
        .filter(
          (p) =>
            !p.isHidden &&
            PROPERTY_TYPES_THAT_CAN_BE_AGGREGATED_BY_ATLEAST_ONE_METHOD.includes(
              p.type,
            ) &&
            !propertyIdsToExclude.includes(p.id),
        )
        .map((p) => ({
          label: p.fullName,
          value: p.id,
          methodsAllowedForColumn: getAggregationMethodsAllowedForPropertyType(
            p.type,
          ),
          fieldIconType: p.fieldIconType,
        }));
    },
    [stepInputDataset],
  );

  return (
    <AggregationDefinitionFieldArray<
      FormValues,
      'aggregationDefinitions',
      AggregationDefinitionRequest
    >
      // all aggregation methods are allowed for workflow aggregations
      allowedAggregationMethods={Object.values(AggregationMethod)}
      childFieldNameForAlias="name"
      childFieldNameForMethod="method"
      childFieldNameForColumn="propertyId"
      fieldName="aggregationDefinitions"
      getAllowedColumnOptions={getAllowedColumnOptions}
    />
  );
}
